import { useRef, type MutableRefObject } from 'react';
import { getContext } from '@fiverr-private/fiverr_context';
import { logger } from '@fiverr-private/obs';
import { useEventListener } from '@fiverr-private/hooks';
import { PACKAGE_CLASSNAME } from '@fiverr-private/header';
import { useScrollCallback } from '../../../../../shared/hooks/useScrollCallback/useScrollCallback';
import { toggleCategoriesMenu } from '../../../../../shared/utils/categoriesMenuToggler';
import { ANCHORS_BAR_SELECTOR } from '../../AnchorsBar/constants';

/** `onFirstShown` fires once, the first time scrolling reveals the categories menu */
export const useCategoriesToggle = (triggerRef: MutableRefObject<HTMLElement | null>, onFirstShown?: () => void) => {
    const { isMobile } = getContext();
    const wasShownRef = useRef(false);
    const anchorsBarRef = useRef<HTMLElement | null | undefined>(undefined);
    const callback = (_event?: Event, unsubscribe?: () => void) => {
        if (isMobile) {
            return;
        }
        const header = document.querySelector<HTMLElement>(`header.${PACKAGE_CLASSNAME}`);
        if (!header) {
            logger.error({
                message: 'Header element not found. CategoriesMenu will be always rendered as a safe fallback',
            });
            toggleCategoriesMenu(true);
            unsubscribe?.();
            return;
        }
        if (!triggerRef.current) {
            logger.error({
                message: 'Scroll trigger element not found. CategoriesMenu will be always rendered as a safe fallback',
            });
            toggleCategoriesMenu(true);
            unsubscribe?.();
            return;
        }
        const triggerRect = triggerRef.current.getBoundingClientRect();
        if (anchorsBarRef.current === undefined) {
            anchorsBarRef.current = document.querySelector<HTMLElement>(ANCHORS_BAR_SELECTOR);
        }
        const anchorsBarRect = anchorsBarRef.current?.getBoundingClientRect();
        // The bar is display:none below md, so fall back to the header.
        const boundary = anchorsBarRect?.height ? anchorsBarRect : header.getBoundingClientRect();
        const isShown = triggerRect.bottom < boundary.bottom;

        toggleCategoriesMenu(isShown);

        if (isShown && !wasShownRef.current) {
            wasShownRef.current = true;
            onFirstShown?.();
        }
    };
    useScrollCallback(!isMobile ? callback : undefined);
    useEventListener('load', callback, typeof window !== 'undefined' ? window : null);
};
